import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

declare function gtag(...args: any[]): void;

const STORAGE_KEY = "cookie-consent";

/** Dispatch this custom event to open the cookie settings modal from anywhere */
export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent("open-cookie-settings"));
}

function updateConsent(granted: boolean) {
  if (typeof gtag === "function") {
    gtag("consent", "update", {
      analytics_storage: granted ? "granted" : "denied",
    });
  }
}

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consentState, setConsentState] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    setConsentState(stored);
    if (stored === "accepted") {
      updateConsent(true);
    }
    if (!stored) {
      setShowBanner(true);
    }

    const handleOpenSettings = () => setShowSettings(true);
    window.addEventListener("open-cookie-settings", handleOpenSettings);
    return () => window.removeEventListener("open-cookie-settings", handleOpenSettings);
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    setConsentState("accepted");
    updateConsent(true);
    setShowBanner(false);
    setShowSettings(false);
  };

  const reject = () => {
    localStorage.setItem(STORAGE_KEY, "rejected");
    setConsentState("rejected");
    updateConsent(false);
    setShowBanner(false);
    setShowSettings(false);
  };

  return (
    <>
      {/* Cookie banner */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6"
          >
            <div
              className="max-w-4xl mx-auto rounded-lg border border-white/10 px-6 py-5 backdrop-blur-xl"
              style={{ background: "rgba(10, 10, 15, 0.92)" }}
            >
              <p className="text-sm leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.75)" }}>
                Tento web používá cookies pro analýzu návštěvnosti. Více informací najdete v našich{" "}
                <a
                  href="/zasady-ochrany-osobnich-udaju"
                  className="underline hover:text-cyan-400 transition-colors"
                  style={{ color: "rgba(255,255,255,0.9)" }}
                >
                  Zásadách ochrany osobních údajů
                </a>
                .
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={accept}
                  className="px-6 py-2.5 rounded-[0.3rem] text-sm font-semibold text-white border border-white hover:[background:white!important] hover:text-black transition-all duration-300"
                  style={{ background: "linear-gradient(135deg, #000000 50%, #00d4ff40 100%)" }}
                >
                  Přijmout vše
                </button>
                <button
                  onClick={reject}
                  className="px-6 py-2.5 rounded-[0.3rem] text-sm font-semibold transition-all duration-300 border"
                  style={{ color: "rgba(255,255,255,0.6)", borderColor: "rgba(255,255,255,0.15)" }}
                >
                  Odmítnout
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie settings modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setShowSettings(false)}
            />
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-md rounded-lg border border-white/10 px-6 py-6 backdrop-blur-xl"
              style={{ background: "rgba(10, 10, 15, 0.95)" }}
            >
              <h3 className="text-lg font-semibold text-white mb-2">Nastavení cookies</h3>
              <p className="text-sm mb-1" style={{ color: "rgba(255,255,255,0.6)" }}>
                Aktuální stav:{" "}
                <span style={{ color: consentState === "accepted" ? "#22d3ee" : "rgba(255,255,255,0.8)" }}>
                  {consentState === "accepted" ? "Povoleny" : "Odmítnuty"}
                </span>
              </p>
              <p className="text-sm mb-5" style={{ color: "rgba(255,255,255,0.5)" }}>
                Analytické cookies nám pomáhají zlepšovat web.{" "}
                <a
                  href="/zasady-ochrany-osobnich-udaju"
                  className="underline hover:text-cyan-400 transition-colors"
                >
                  Více informací
                </a>
                .
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={accept}
                  className="px-6 py-2.5 rounded-[0.3rem] text-sm font-semibold text-white border border-white hover:[background:white!important] hover:text-black transition-all duration-300"
                  style={{ background: "linear-gradient(135deg, #000000 50%, #00d4ff40 100%)" }}
                >
                  Přijmout vše
                </button>
                <button
                  onClick={reject}
                  className="px-6 py-2.5 rounded-[0.3rem] text-sm font-semibold transition-all duration-300 border"
                  style={{ color: "rgba(255,255,255,0.6)", borderColor: "rgba(255,255,255,0.15)" }}
                >
                  Odmítnout
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-6 py-2.5 rounded-[0.3rem] text-sm font-semibold transition-all duration-300"
                  style={{ color: "rgba(255,255,255,0.4)" }}
                >
                  Zavřít
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
